import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EyeIcon, EyeSlashIcon, LockClosedIcon, ShieldCheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { validateCredentials, getAllRoles, getSecurityConfig } from '../config/auth';
import Logo from './Logo';

const LoginGate = ({ onLogin }) => {
  const [selectedRole, setSelectedRole] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [showAccessCode, setShowAccessCode] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutEndTime, setLockoutEndTime] = useState(null);

  const roles = getAllRoles();
  const securityConfig = getSecurityConfig();

  // Check for existing lockout on component mount
  useEffect(() => {
    const lockoutData = localStorage.getItem('valeon_lockout');
    if (lockoutData) {
      const { endTime, attempts } = JSON.parse(lockoutData);
      const now = Date.now();
      
      if (now < endTime) {
        setIsLocked(true);
        setLockoutEndTime(endTime);
        setAttemptCount(attempts);
      } else {
        // Lockout expired, clear it
        localStorage.removeItem('valeon_lockout');
      }
    }
  }, []);

  // Update lockout timer
  useEffect(() => {
    if (isLocked && lockoutEndTime) {
      const timer = setInterval(() => {
        const now = Date.now();
        if (now >= lockoutEndTime) {
          setIsLocked(false);
          setLockoutEndTime(null);
          setAttemptCount(0);
          localStorage.removeItem('valeon_lockout');
          clearInterval(timer);
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isLocked, lockoutEndTime]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLocked) {
      setError('Account temporarily locked. Please wait before trying again.');
      return;
    }

    setError('');
    setIsLoading(true);

    // Simulate loading for better UX
    await new Promise(resolve => setTimeout(resolve, 1200));

    if (!selectedRole) {
      setError('Please select your access level');
      setIsLoading(false);
      return;
    }

    if (!accessCode) {
      setError('Please enter your access code');
      setIsLoading(false);
      return;
    }

    const isValid = validateCredentials(selectedRole, accessCode);
    
    if (isValid) {
      // Successful login - clear any lockout data
      localStorage.removeItem('valeon_lockout');
      setAttemptCount(0);
      
      // Call the AuthContext login function which handles all auth logic
      onLogin(selectedRole);
    } else {
      const newAttemptCount = attemptCount + 1;
      setAttemptCount(newAttemptCount);
      
      if (newAttemptCount >= securityConfig.maxAttempts) {
        // Trigger lockout
        const lockoutEndTime = Date.now() + securityConfig.lockoutDuration;
        setIsLocked(true);
        setLockoutEndTime(lockoutEndTime);
        
        localStorage.setItem('valeon_lockout', JSON.stringify({
          endTime: lockoutEndTime,
          attempts: newAttemptCount
        }));
        
        setError(`Too many failed attempts. Access locked for ${securityConfig.lockoutDuration / 60000} minutes.`);
      } else {
        const remainingAttempts = securityConfig.maxAttempts - newAttemptCount;
        setError(`Invalid access code. ${remainingAttempts} attempt${remainingAttempts !== 1 ? 's' : ''} remaining.`);
      }
    }
    
    setIsLoading(false);
  };

  const handleRoleSelect = (roleId) => {
    if (isLocked) return;
    
    setSelectedRole(roleId);
    setAccessCode('');
    setError('');
  };

  const getRemainingLockoutTime = () => {
    if (!lockoutEndTime) return '';
    
    const remaining = Math.ceil((lockoutEndTime - Date.now()) / 1000);
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-primary-900"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-accent-blue rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-accent-gold rounded-full blur-3xl"></div>
        </div>
      </div>

      {/* Login Form */}
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="relative z-10 w-full max-w-md mx-4"
      >
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-4"
          >
            <Logo size="xl" variant="circular" className="mx-auto" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-heading-lg font-serif font-bold text-cream-100 mb-2"
          >
            Valeon Strategic Advisory
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="text-cream-300 text-sm mb-2"
          >
            Smarter Liquidity, Realized
          </motion.p>

          {isLocked && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center justify-center space-x-2 text-red-400 text-sm bg-red-400/10 border border-red-400/20 rounded-lg p-2 mt-4"
            >
              <ExclamationTriangleIcon className="h-4 w-4" />
              <span>Access locked • {getRemainingLockoutTime()}</span>
            </motion.div>
          )}
        </div>

        {/* Login Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1 }}
          className="glass-card p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Role Selection */}
            <div>
              <label className="block text-cream-100 text-sm font-medium mb-4 flex items-center space-x-2">
                <ShieldCheckIcon className="h-4 w-4" />
                <span>Select Your Access Level</span>
              </label>
              <div className="space-y-3">
                {roles.map((role) => (
                  <motion.button
                    key={role.id}
                    type="button"
                    onClick={() => handleRoleSelect(role.id)}
                    disabled={isLocked}
                    whileHover={!isLocked ? { scale: 1.02 } : {}}
                    whileTap={!isLocked ? { scale: 0.98 } : {}}
                    className={`w-full p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                      selectedRole === role.id
                        ? 'border-accent-blue bg-accent-blue/10 shadow-lg'
                        : 'border-white/20 bg-white/5 hover:border-white/30 hover:bg-white/10'
                    } ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        selectedRole === role.id 
                          ? 'border-accent-blue bg-accent-blue' 
                          : 'border-white/40'
                      }`}>
                        {selectedRole === role.id && (
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="text-cream-100 font-medium">{role.label}</div>
                        <div className="text-cream-300 text-xs">{role.description}</div>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Access Code Input */}
            <AnimatePresence>
              {selectedRole && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <label className="block text-cream-100 text-sm font-medium mb-2">
                    Enter Your Access Code
                  </label>
                  <div className="relative">
                    <input
                      type={showAccessCode ? 'text' : 'password'}
                      value={accessCode}
                      onChange={(e) => setAccessCode(e.target.value)}
                      placeholder="••••••••"
                      disabled={isLocked}
                      className={`w-full px-4 py-3 pl-10 pr-10 bg-white/10 border border-white/20 rounded-xl text-cream-100 placeholder-cream-400 focus:outline-none focus:border-accent-blue focus:bg-white/15 transition-all duration-300 ${
                        isLocked ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      required
                    />
                    <LockClosedIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-cream-400" />
                    <button
                      type="button"
                      onClick={() => setShowAccessCode(!showAccessCode)}
                      disabled={isLocked}
                      className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-cream-400 hover:text-cream-200 transition-colors duration-200 ${
                        isLocked ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {showAccessCode ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Error Message */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className={`text-sm text-center rounded-lg p-3 ${
                    isLocked 
                      ? 'text-red-400 bg-red-400/10 border border-red-400/20'
                      : 'text-orange-400 bg-orange-400/10 border border-orange-400/20'
                  }`}
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={!selectedRole || !accessCode || isLoading || isLocked}
              whileHover={{ scale: selectedRole && accessCode && !isLoading && !isLocked ? 1.02 : 1 }}
              whileTap={{ scale: selectedRole && accessCode && !isLoading && !isLocked ? 0.98 : 1 }}
              className={`w-full py-3 px-4 rounded-xl font-medium transition-all duration-300 ${
                selectedRole && accessCode && !isLoading && !isLocked
                  ? 'bg-gradient-to-r from-accent-blue to-accent-gold text-white hover:shadow-lg hover:shadow-accent-blue/30'
                  : 'bg-white/10 text-cream-400 cursor-not-allowed'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Authenticating...</span>
                </div>
              ) : isLocked ? (
                'Access Temporarily Locked'
              ) : (
                'Access Platform'
              )}
            </motion.button>
          </form>

          {/* Security Notice */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.4 }}
            className="mt-6 pt-6 border-t border-white/10"
          >
            <div className="text-center text-cream-400 text-xs">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <ShieldCheckIcon className="h-3 w-3" />
                <span className="font-medium">Secure Access Portal</span>
              </div>
              <div>
                Protected by enterprise-grade security protocols
              </div>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default LoginGate;